import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Trophy, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;

const poll = async (jobId) => {
  for (let k = 0; k < 120; k++) {
    await new Promise((r) => setTimeout(r, 3000));
    try {
      const { data } = await axios.get(`${API}/upload-jobs/${jobId}`);
      if (data.status === 'done') return data.result;
      if (data.status === 'error') throw new Error(data.error || 'Could not read the slips');
    } catch (e) { if (!e.response || (e.response.status !== 502 && e.response.status !== 504)) throw e; }
  }
  throw new Error('Reading took too long — try fewer slips at a time');
};

const SlipRow = ({ s, i, onChange }) => (
  <div className="rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-2 space-y-1.5" data-testid={`winning-slip-${i}`}>
    <p className="text-xs font-bold text-white">{(s.legs || []).length}-leg {s.platform || s.sportsbook || ''} slip <span className="font-normal text-zinc-400">· {(s.legs || []).slice(0, 3).map((l) => l.selection).join(' · ')}{(s.legs || []).length > 3 ? ' …' : ''}</span></p>
    <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-400">
      <label>Bet $<input type="number" inputMode="decimal" value={s.stake ?? ''} onChange={(e) => onChange({ stake: e.target.value })} className="ml-1 w-20 rounded border border-zinc-700 bg-zinc-950 px-1.5 py-0.5 text-white" data-testid={`winning-stake-${i}`} /></label>
      <label>Paid $<input type="number" inputMode="decimal" value={s.potential_payout ?? ''} onChange={(e) => onChange({ potential_payout: e.target.value })} className="ml-1 w-24 rounded border border-zinc-700 bg-zinc-950 px-1.5 py-0.5 text-white" data-testid={`winning-payout-${i}`} /></label>
      <label>Night <input type="date" value={s.night || ''} onChange={(e) => onChange({ night: e.target.value })} className="ml-1 rounded border border-zinc-700 bg-zinc-950 px-1.5 py-0.5 text-white" data-testid={`winning-night-${i}`} /></label>
      <button type="button" onClick={() => onChange(null)} className="text-zinc-500 hover:text-red-400" data-testid={`winning-remove-${i}`}>Remove</button>
    </div>
  </div>
);

// Upload Kalshi / FanDuel slips that paid out → saved as WON in Settled, counted in the money history.
export default function WinningSlips({ onSaved }) {
  const input = useRef(null);
  const [busy, setBusy] = useState('');
  const [slips, setSlips] = useState([]);
  const read = async (files) => {
    if (!files.length) return;
    setBusy('read');
    try {
      const fd = new FormData();
      [...files].forEach((f) => fd.append('files', f));
      const { data } = await axios.post(`${API}/upload`, fd);
      const res = await poll(data.job_id);
      const found = res?.parlays || [];
      if (!found.length) toast.error('No slips found in those files');
      setSlips((cur) => [...cur, ...found.map((p) => ({ ...p, night: '' }))]);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy('');
    if (input.current) input.current.value = '';
  };
  const change = (i, patch) => setSlips((cur) => (patch ? cur.map((s, k) => (k === i ? { ...s, ...patch } : s)) : cur.filter((_, k) => k !== i)));
  const saveAll = async () => {
    setBusy('save');
    let ok = 0;
    const left = [];
    for (const s of slips) {
      try {
        await axios.post(`${API}/parlays/winning`, { ...s, stake: Number(s.stake) || 0, potential_payout: Number(s.potential_payout) || 0 });
        ok++;
      } catch (e) { left.push(s); toast.error(e.response?.data?.detail || e.message); }
    }
    setSlips(left);
    setBusy('');
    if (ok) {
      toast.success(`${ok} winning slip${ok > 1 ? 's' : ''} saved to Won`);
      window.dispatchEvent(new Event('ticket-money-changed'));
      onSaved?.();
    }
  };
  return (
    <div className="space-y-2 rounded-xl border border-emerald-600/40 bg-zinc-900/70 p-3" data-testid="winning-slips">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-black text-emerald-300"><Trophy className="h-4 w-4" />Upload winning slips</p>
        <button type="button" onClick={() => input.current?.click()} disabled={!!busy} data-testid="winning-upload-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-black text-black hover:bg-emerald-400 disabled:opacity-50">
          {busy === 'read' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}{busy === 'read' ? 'Reading…' : 'Choose screenshots'}
        </button>
        <input ref={input} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => read(e.target.files)} data-testid="winning-file-input" />
      </div>
      <p className="text-[11px] text-zinc-500">Screenshots of Kalshi or FanDuel slips that paid. Each is saved as WON with what it paid. Leave Night empty to keep the night it's already on in the app (or today if it's new).</p>
      {slips.map((s, i) => <SlipRow key={`${i}-${(s.legs || []).length}`} s={s} i={i} onChange={(p) => change(i, p)} />)}
      {slips.length > 0 && (
        <button type="button" onClick={saveAll} disabled={!!busy} data-testid="winning-save-all"
          className="w-full rounded-lg bg-emerald-500 py-2 text-sm font-black text-black hover:bg-emerald-400 disabled:opacity-50">
          {busy === 'save' ? 'Saving…' : `Save ${slips.length} as won`}
        </button>
      )}
    </div>
  );
}
